import i18n from 'i18next';
import type { AnyTFunction, TKey } from './msg';

/**
 * Label maps (#2109 R3-B4).
 *
 * A module-level `Record<Enum, string>` of English labels becomes a KEY map —
 * `GRANT_STATUS_LABEL_KEY: Record<GrantStatus, TKey>`, declared `as const` —
 * and the component resolves it at render with `labelFor(map, value, t)`.
 * Search that matched the English label matches `labelFor`’s result instead.
 *
 * A map that staff files also import keeps its old name as
 * `englishLabels(map)`: an object with one getter per value that reads the
 * EN catalog whatever the active language, so an R4 file keeps compiling and
 * keeps its English unchanged. R4 deletes the getter.
 *
 * Every map gets a parity test: `missingLabelKeys(map, ALL_VALUES)` is `[]`.
 */

type LooseT = (key: string, params?: Record<string, unknown>) => string;

/** `t(map[value])`. An unknown or empty value renders `fallback`, else the raw value — never a key path. */
export function labelFor<V extends string>(
  map: Readonly<Record<V, TKey>>,
  value: V | null | undefined,
  t: AnyTFunction,
  fallback?: string,
): string {
  if (value == null || value === '') return fallback ?? '';
  // Read as a plain string. `i18n.exists` is a type guard, and narrowing a
  // TKey (the whole key union) compares its members pairwise: past ~4,000
  // keys that overflows the checker ("Map maximum size exceeded", R3
  // integration). A plain string keeps every helper cheap to check.
  const key = (map as Readonly<Record<string, string | undefined>>)[value];
  if (!key) return fallback ?? String(value);
  return (t as unknown as LooseT)(key);
}

/** The English compatibility getter: `englishLabels(X_LABEL_KEY).value` reads the en catalog lazily. */
export function englishLabels<V extends string>(map: Readonly<Record<V, TKey>>): Readonly<Record<V, string>> {
  const out = {} as Record<V, string>;
  for (const value of Object.keys(map) as V[]) {
    Object.defineProperty(out, value, {
      enumerable: true,
      get: () => (i18n.getFixedT('en') as unknown as LooseT)(map[value]),
    });
  }
  return Object.freeze(out);
}

/**
 * The parity check a per-map test runs: every enum value has a key, and every
 * key exists in the EN catalog. Returns one line per defect (empty = green).
 */
export function missingLabelKeys<V extends string>(
  map: Readonly<Record<string, TKey>>,
  values: readonly V[],
): string[] {
  const out: string[] = [];
  for (const value of values) {
    const key: string | undefined = map[value];
    if (!key) out.push(`${value} (no key)`);
    else if (!i18n.exists(key, { lng: 'en' })) out.push(`${value} → ${key} (not in the en catalog)`);
  }
  return out;
}
